import {
  buildMedicalDocumentDuplicateSignature,
  extractMedicalDocumentStoragePath,
  getStoredMedicalDocumentFingerprint,
  isIgnoredMedicalDocumentRecord
} from './medical-document-storage'
import type { MedicalDocument } from '@/types/health'

const INVOICE_FALLBACK_RECORD_TYPE = 'invoice_fallback'
const INVOICE_FALLBACK_TAG = 'invoice-fallback'
const MANUAL_LAB_ENTRY_RECORD_TYPE = 'manual_lab_entry'

function getSafeExtractedData(document: Pick<MedicalDocument, 'extracted_data'>): Record<string, unknown> {
  if (document.extracted_data && typeof document.extracted_data === 'object' && !Array.isArray(document.extracted_data)) {
    return document.extracted_data as Record<string, unknown>
  }

  return {}
}

export function isInvoiceFallbackMedicalDocument(document: Pick<MedicalDocument, 'extracted_data' | 'tags'>): boolean {
  const extractedData = getSafeExtractedData(document as Pick<MedicalDocument, 'extracted_data'>)

  return (
    extractedData.system_record_type === INVOICE_FALLBACK_RECORD_TYPE ||
    document.tags?.includes(INVOICE_FALLBACK_TAG) === true
  )
}

export function isManualLabEntryMedicalDocument(document: Pick<MedicalDocument, 'extracted_data'>): boolean {
  const extractedData = getSafeExtractedData(document as Pick<MedicalDocument, 'extracted_data'>)
  return extractedData.system_record_type === MANUAL_LAB_ENTRY_RECORD_TYPE
}

export function isDisplayableMedicalDocument(document: MedicalDocument): boolean {
  return (
    !isIgnoredMedicalDocumentRecord(document) &&
    !isInvoiceFallbackMedicalDocument(document) &&
    !isManualLabEntryMedicalDocument(document)
  )
}

export function filterDisplayableMedicalDocuments(documents: MedicalDocument[]): MedicalDocument[] {
  return documents.filter(isDisplayableMedicalDocument)
}

export function buildMedicalDocumentDisplayDuplicateKey(
  document: Pick<MedicalDocument, 'id' | 'file_name' | 'file_size_bytes' | 'extracted_data'>
): string {
  const extractedData = getSafeExtractedData(document as Pick<MedicalDocument, 'extracted_data'>)
  const fingerprint = getStoredMedicalDocumentFingerprint(extractedData)

  if (fingerprint) {
    return `fingerprint:${fingerprint}`
  }

  const extractedSignature =
    typeof extractedData.duplicate_signature_v1 === 'string' && extractedData.duplicate_signature_v1.trim().length > 0
      ? extractedData.duplicate_signature_v1.trim()
      : null

  if (extractedSignature) {
    return `signature:${extractedSignature}`
  }

  const fallbackSignature = buildMedicalDocumentDuplicateSignature(
    document.file_name,
    document.file_size_bytes
  )

  if (fallbackSignature) {
    return `signature:${fallbackSignature}`
  }

  return `document:${document.id}`
}

function getMedicalDocumentPriorityScore(
  document: Pick<MedicalDocument, 'file_name' | 'file_url' | 'file_size_bytes' | 'extracted_data'> & {
    storage_path?: string | null
  }
): number {
  const extractedData = getSafeExtractedData(document as Pick<MedicalDocument, 'extracted_data'>)
  let score = 0

  const storagePath =
    (typeof document.storage_path === 'string' && document.storage_path.trim()) ||
    extractMedicalDocumentStoragePath(document.file_url)

  if (storagePath) {
    score += 1000
  }

  if (document.file_url) {
    score += 200
  }

  if (typeof document.file_size_bytes === 'number' && Number.isFinite(document.file_size_bytes) && document.file_size_bytes > 0) {
    score += 50
  }

  if (getStoredMedicalDocumentFingerprint(extractedData)) {
    score += 30
  }

  if (typeof extractedData.manual_annotation === 'string' && extractedData.manual_annotation.trim()) {
    score += 20
  }

  if (typeof document.file_name === 'string' && document.file_name.trim()) {
    score += 10
  }

  return score
}

function getMedicalDocumentTimestampValue(document: Pick<MedicalDocument, 'document_date' | 'created_at'>): number {
  const documentDate = Date.parse(document.document_date)
  if (Number.isFinite(documentDate)) {
    return documentDate
  }

  const createdAt = Date.parse(document.created_at)
  if (Number.isFinite(createdAt)) {
    return createdAt
  }

  return 0
}

export function compareMedicalDocumentsForDisplay(
  left: Pick<MedicalDocument, 'id' | 'document_date' | 'created_at' | 'file_name' | 'file_url' | 'file_size_bytes' | 'extracted_data'> & {
    storage_path?: string | null
  },
  right: Pick<MedicalDocument, 'id' | 'document_date' | 'created_at' | 'file_name' | 'file_url' | 'file_size_bytes' | 'extracted_data'> & {
    storage_path?: string | null
  }
): number {
  const priorityDelta = getMedicalDocumentPriorityScore(right) - getMedicalDocumentPriorityScore(left)

  if (priorityDelta !== 0) {
    return priorityDelta
  }

  const timestampDelta = getMedicalDocumentTimestampValue(right) - getMedicalDocumentTimestampValue(left)

  if (timestampDelta !== 0) {
    return timestampDelta
  }

  return right.id.localeCompare(left.id)
}
